import {triggersApi} from 'sources/api';
import cancelIfSending from 'sources/cancelIfSending';
import {List, Map} from 'immutable';
import keyBy from 'lodash/keyBy';
import TriggerRuleInfo from 'records/TriggerRuleInfo';
import TriggerPermission from 'records/TriggerPermission';
import TriggerRuleAction from 'records/TriggerRuleAction';
import TriggerTrend from 'records/TriggerTrend';
import AntiRule from 'records/AntiRule';
import {TagInfo} from 'records/TagInfo';

const TriggersSource = cancelIfSending({
  fetchTriggerRules() {
    return triggersApi.getJson('/triggers/list').then(response => {
      return Map(
        keyBy(
          response.map(item => new TriggerRuleInfo(item)),
          i => i.id.toString()
        )
      );
    });
  },

  fetchTriggerRule(ruleId) {
    return triggersApi
      .getJson(`/triggers/${ruleId}`)
      .then(response => new TriggerRuleInfo(response));
  },

  fetchTriggerRulePermissions(ruleId) {
    return triggersApi.getJson(`/triggers/${ruleId}/permissions`).then(permissions => {
      return List(permissions).map(permission => new TriggerPermission(permission));
    });
  },

  fetchTriggerRuleTags(ruleId) {
    return triggersApi
      .getJson(`/triggers/${ruleId}/tags`)
      .then(tags => List(tags).map(tag => new TagInfo(tag)));
  },

  updateTriggerRuleTags(ruleId, tags) {
    return triggersApi.postJson(`/triggers/${ruleId}/tags`, tags);
  },

  updateTriggerRulePermissions(ruleId, permissions) {
    return triggersApi.postJson(`/triggers/${ruleId}/permissions`, permissions);
  },

  createTriggerRule(ruleInfo) {
    return triggersApi.postJson('/triggers', JSON.stringify(ruleInfo)).then(response => {
      const rule = new TriggerRuleInfo(response);
      // Should be done in the backend, see EV-11719, we need the EDIT permission
      // so that the edit icon will show after rule creation
      return rule.setIn(['rulePermissions', 'EDIT'], true);
    });
  },

  cloneTriggerRule(ruleId) {
    return triggersApi
      .getJson(`/triggers/${ruleId}/clone`)
      .then(response => new TriggerRuleInfo(response));
  },

  removeTriggerRule(ruleId) {
    return triggersApi.delete(`/triggers/${ruleId}/`);
  },

  updateTriggerRule(ruleId, ruleInfo) {
    return triggersApi
      .putJson(`/triggers/${ruleId}`, JSON.stringify(ruleInfo))
      .then(response => new TriggerRuleInfo(response));
  },

  updateTriggerRuleDamping(ruleId, damping) {
    return triggersApi
      .putJson(`/triggers/${ruleId}/damping`, JSON.stringify({value: damping}))
      .then(response => new TriggerRuleInfo(response));
  },

  fetchTriggerRuleActions(ruleId) {
    return triggersApi.getJson(`/triggers/${ruleId}/actions`).then(response => {
      return Map(
        keyBy(
          response
            .map(item => new TriggerRuleAction(item))
            .filter(action => !!action.configuration),
          i => i.id.toString()
        )
      );
    });
  },

  createTriggerRuleAction(ruleId, triggerRuleAction) {
    const ruleAction = triggerRuleAction.toEntity();
    return triggersApi
      .postJson(`/triggers/${ruleId}/actions`, JSON.stringify(ruleAction))
      .then(response => new TriggerRuleAction(response));
  },

  updateTriggerRuleAction(ruleId, actionId, triggerRuleAction) {
    const ruleAction = triggerRuleAction.toEntity();
    return triggersApi
      .putJson(`/triggers/${ruleId}/actions/${actionId}`, JSON.stringify(ruleAction))
      .then(response => new TriggerRuleAction(response));
  },

  removeTriggerRuleAction(ruleId, actionId) {
    return triggersApi.delete(`/triggers/${ruleId}/actions/${actionId}`);
  },

  updateTriggerTrend(ruleId, triggerTrend) {
    return triggersApi
      .putJson(`/triggers/${ruleId}/trends/${triggerTrend.id}`, JSON.stringify(triggerTrend))
      .then(trend => new TriggerTrend(trend));
  },

  getTriggerTrends(ruleId) {
    return triggersApi.getJson(`/triggers/${ruleId}/trends`).then(response => {
      return new Map(
        keyBy(
          response.map(item => new TriggerTrend(item)),
          i => i.id.toString()
        )
      );
    });
  },

  createTriggerTrend(ruleId, trend) {
    return triggersApi
      .postJson(`/triggers/${ruleId}/trends`, JSON.stringify(trend))
      .then(trend => new TriggerTrend(trend));
  },

  fetchAntiRules(ruleId) {
    return triggersApi.getJson(`/triggers/${ruleId}/anti_rules`).then(
      antiRules => [ruleId, List(antiRules).map(rule => new AntiRule(rule))],
      error => {
        // eslint-disable-next-line
        throw [ruleId, error];
      }
    );
  },

  updateTriggerName(ruleId, name) {
    return triggersApi
      .patchJson(`/triggers/${ruleId}/name`, name, {
        params: {resetName: name === undefined},
      })
      .then(rule => new TriggerRuleInfo(rule));
  },
});

export default TriggersSource;
